import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import CrmLayout from "@/components/feature/CrmLayout";
import { QuotationStatsBar } from "./components/QuotationStatsBar";
import { QuotationFilterBar } from "./components/QuotationFilterBar";
import { QuotationTable } from "./components/QuotationTable";
import { exportToCSV, formatExportDate } from "@/utils/exportUtils";
import { useToast } from "@/components/base/Toast";
import type { QuotationStatus, Quotation } from "@/mocks/quotationsData";
import { fetchApi } from "@/services/apiService";

export default function QuotationsPage() {
  const { showToast } = useToast();
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [agentsList, setAgentsList] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<QuotationStatus | "All">("All");
  const [dateFilter, setDateFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  // 1. Fetch Dynamic Quotations
  const loadQuotations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchApi<any>("/quotations", undefined, []);
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setQuotations(list);
    } catch (err: any) {
      console.error("Error loading quotations:", err);
      showToast("Failed to load quotations from server", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadQuotations();
  }, [loadQuotations]);

  // 2. Fetch Dynamic Agents from Backend
  useEffect(() => {
    fetchApi<any>("/auth/company-user/all", undefined, null)
      .then((res) => {
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const names: string[] = [];
        userList.forEach((u: any) => {
          if (u.name) names.push(u.name);
          else if (u.fullName) names.push(u.fullName);
          else if (u.username) names.push(u.username);
          if (u.leader_name) names.push(u.leader_name);
          if (Array.isArray(u.member_names)) names.push(...u.member_names);
        });

        // Also add unique agents from existing quotations
        quotations.forEach((q) => {
          if (q.agent) names.push(q.agent);
        });

        const unique = Array.from(new Set(names)).filter(Boolean);
        if (unique.length > 0) {
          setAgentsList(unique);
        }
      })
      .catch(() => {});
  }, [quotations]);

  // Close export dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    };
    if (exportOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [exportOpen]);

  const filtered = useMemo(() => {
    let result = quotations;

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (qt) =>
          (qt.leadName && qt.leadName.toLowerCase().includes(q)) ||
          ((qt as any).client_name && (qt as any).client_name.toLowerCase().includes(q)) ||
          (qt.leadCompany && qt.leadCompany.toLowerCase().includes(q)) ||
          ((qt as any).client_company && (qt as any).client_company.toLowerCase().includes(q)) ||
          (qt.quoteNo && qt.quoteNo.toLowerCase().includes(q)) ||
          ((qt as any).quote_number && (qt as any).quote_number.toLowerCase().includes(q)) ||
          (qt.agent && qt.agent.toLowerCase().includes(q))
      );
    }

    if (statusFilter !== "All") {
      result = result.filter((qt) => qt.status === statusFilter);
    }

    if (agentFilter) {
      result = result.filter((qt) => qt.agent === agentFilter);
    }

    if (dateFilter) {
      const now = new Date();
      const today = now.toISOString().split("T")[0];
      if (dateFilter === "Today") {
        result = result.filter((qt) => {
          const d = qt.date || (qt as any).quote_date || "";
          return d.startsWith(today);
        });
      } else if (dateFilter === "This Week") {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        const weekAgoStr = weekAgo.toISOString().split("T")[0];
        result = result.filter((qt) => {
          const d = qt.date || (qt as any).quote_date || "";
          return d >= weekAgoStr;
        });
      } else if (dateFilter === "This Month") {
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        const monthAgoStr = monthAgo.toISOString().split("T")[0];
        result = result.filter((qt) => {
          const d = qt.date || (qt as any).quote_date || "";
          return d >= monthAgoStr;
        });
      }
    }

    return result;
  }, [quotations, search, statusFilter, dateFilter, agentFilter]);

  // ── Export handlers ──
  const handleExportAllQuotations = () => {
    showToast("Preparing export...", "info");
    const rows = quotations.map((q) => {
      const items = q.items || [];
      const total = Number(q?.total_amount) || items.reduce((sum, i) => sum + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);
      return {
        "Quote No": q.quoteNo || (q as any).quote_number,
        Lead: q.leadName || (q as any).client_name,
        Company: q.leadCompany || (q as any).client_company,
        "Items Count": items.length,
        Amount: `₹${total.toLocaleString("en-IN")}`,
        Status: q.status,
        "Valid Until": q.validUntil || (q as any).valid_until,
        "Created By": q.agent,
      };
    });
    setTimeout(() => {
      exportToCSV(rows, `quotations-${formatExportDate()}.csv`);
      showToast("Download started", "success");
    }, 400);
    setExportOpen(false);
  };

  const handleExportAccepted = () => {
    showToast("Preparing export...", "info");
    const accepted = quotations.filter((q) => q.status === "Accepted");
    const rows = accepted.map((q) => {
      const items = q.items || [];
      const total = Number(q.total_amount) || items.reduce((sum, i) => sum + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);
      return {
        "Quote No": q.quoteNo || (q as any).quote_number,
        Lead: q.leadName || (q as any).client_name,
        Company: q.leadCompany || (q as any).client_company,
        "Items Count": items.length,
        Amount: `₹${total.toLocaleString("en-IN")}`,
        Status: q.status,
        "Valid Until": q.validUntil || (q as any).valid_until,
        "Created By": q.agent,
      };
    });
    setTimeout(() => {
      exportToCSV(rows, `quotations-accepted-${formatExportDate()}.csv`);
      showToast("Download started", "success");
    }, 400);
    setExportOpen(false);
  };
  // ── End export handlers ──

  return (
    <CrmLayout title="Quotations">
      <div className="p-5 md:p-6 space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Quotations</h1>
            <p className="text-sm text-gray-500 mt-0.5">Create and manage proposals for leads</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/quotations/templates"
              className="inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-indigo-600 border border-indigo-600 rounded-lg hover:bg-indigo-50 transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-file-copy-line" />
              Templates
            </a>
            <div className="relative" ref={exportRef}>
              <button
                onClick={() => setExportOpen(!exportOpen)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-line" /> Export
              </button>
              {exportOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-200 shadow-lg w-56 py-2 z-50">
                  <button
                    onClick={handleExportAllQuotations}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-file-excel-2-line text-green-600" />
                    Export All Quotations CSV
                  </button>
                  <button
                    onClick={handleExportAccepted}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-check-double-line text-green-500" />
                    Export Accepted Quotations
                  </button>
                </div>
              )}
            </div>
            <a
              href="/quotations/new"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-add-line" />
              + New Quotation
            </a>
          </div>
        </div>

        {/* Dynamic Stats Bar */}
        <QuotationStatsBar quotations={quotations} />

        {/* Dynamic Filter Bar */}
        <QuotationFilterBar
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          dateFilter={dateFilter}
          onDateChange={setDateFilter}
          agentFilter={agentFilter}
          onAgentChange={setAgentFilter}
          agents={agentsList}
        />

        {/* Content Table / Empty state / Loading state */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 py-16 flex flex-col items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-indigo-600 mb-2" />
            <p className="text-sm text-gray-500">Loading quotations...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 py-16 flex flex-col items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <i className="ri-file-search-line text-2xl text-gray-300" />
            </div>
            <h3 className="text-base font-semibold text-gray-700 mb-1">No quotations found</h3>
            <p className="text-sm text-gray-500 mb-4">Try adjusting your filters or create a new quotation</p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setSearch("");
                  setStatusFilter("All");
                  setDateFilter("");
                  setAgentFilter("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Clear Filters
              </button>
              <a
                href="/quotations/new"
                className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                + New Quotation
              </a>
            </div>
          </div>
        ) : (
          <QuotationTable quotations={filtered} onRefresh={loadQuotations} />
        )}
      </div>
    </CrmLayout>
  );
}